import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  CancelSeasonResponse,
  SeasonApiRecord,
  SeasonsListResponse,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { unwrapGamePassEntity } from "./responseMappers";

const buildCancelSeasonUrl = (seasonId: string): string =>
  API_ROUTES.CANCEL_SEASON.replace("{season_id}", seasonId);

const postCancelSeason = async (seasonId: string): Promise<SeasonApiRecord> => {
  const response = await getPrivateInstance().post<CancelSeasonResponse>(
    buildCancelSeasonUrl(seasonId),
    {},
  );
  return unwrapGamePassEntity(response.data, "season");
};

export const useCancelSeason = (): UseMutationResult<
  SeasonApiRecord,
  Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "seasons", "cancel"],
    mutationFn: postCancelSeason,
    onSuccess: (season) => {
      queryClient.setQueriesData<SeasonsListResponse>(
        { queryKey: ["game-pass", "seasons", "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            seasons: current.seasons.map((record) =>
              (record.id ?? record.season_id) === (season.id ?? season.season_id)
                ? { ...record, ...season }
                : record,
            ),
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
    },
  });
};
